import { Autocomplete, Box, Button, TextField } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBulkUpdateDevices } from '~/api/devices';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import type { DeviceBulkSelectionInput } from '~/generated/graphql';
import { assert } from '~/lib/assert';

export interface BulkTagDialogProps {
  deviceTags: string[];
  onClose: () => void;
  onComplete: () => void;
  open: boolean;
  selected: DeviceBulkSelectionInput;
}

export const BulkTagDialog = ({
  deviceTags,
  onClose,
  onComplete,
  open,
  selected,
}: BulkTagDialogProps) => {
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const { t } = useTranslation();

  const [updateDeviceGroup, { loading }] = useBulkUpdateDevices();

  const handleClose = useCallback(() => {
    setSelectedTag(() => null);
    onClose();
  }, [onClose]);

  return (
    <Dialog maxWidth="xs" fullWidth onClose={handleClose} open={open}>
      <DialogTitle onClose={handleClose}>{t('pages.devices.bulk_actions.tags.title')}</DialogTitle>

      <DialogContent>
        <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
          <Autocomplete
            freeSolo
            fullWidth
            onChange={(_event, option) => setSelectedTag(option ?? null)}
            onInputChange={(_, value) => setSelectedTag(value)}
            options={deviceTags}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label={t('common.tags.choose_tag')}
                variant="outlined"
              />
            )}
            value={selectedTag || null}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          {t('common.dialogs.cancel')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={loading || !selectedTag}
          onClick={async () => {
            assert(selectedTag != null, 'tag is null');
            await updateDeviceGroup({
              variables: {
                input: { selected, patch: { tag: selectedTag } },
              },
            });
            handleClose();
            onComplete();
          }}
        >
          {t('common.dialogs.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
